import React from 'react'
import styled, { css } from 'styled-components'
import { theme } from '~/src/theme'
import { Icon } from '~/src/components'

type Variant = 'primary' | 'clear' | 'danger'

type Size = 'sm ' | 'regular'

interface Props {
  block?: boolean
  variant?: Variant
  size?: Size
  icon?: string
  type?: 'button' | 'submit'
  disabled?: boolean
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  children: React.ReactNode
}

const Button: React.FC<Props> = ({
  children,
  icon,
  variant = 'primary',
  size = 'regular',
  type = 'button',
  onClick,
  block = false,
  disabled = false
}) => {
  const $icon = icon ? (
    <IconContainer>
      <Icon name={icon} />
    </IconContainer>
  ) : null

  return (
    <Element variant={variant} size={size} type={type} onClick={onClick} block={block} disabled={disabled}>
      <span>{children}</span>
      {$icon}
    </Element>
  )
}

const Element = styled.button<{ block?: boolean; variant?: Variant; size?: Size }>`
  display: ${(props) => (props.block ? 'flex' : 'inline-flex')};
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  color: ${theme.colors.neutral[900]};
  background: transparent;
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 500;
  text-align: center;
  border: 0;
  border-radius: 4px;
  transition: 200ms all ease;
  cursor: pointer;

  ${(props) =>
    props.variant === 'primary' &&
    css`
      color: ${theme.colors.neutral[50]};
      background: ${theme.colors.blue[500]};
    `}

  ${(props) =>
    props.variant === 'danger' &&
    css`
      background: ${theme.colors.red[500]};
    `}

    ${(props) =>
    props.variant === 'clear' &&
    css`
      color: ${theme.colors.neutral[600]};
      border: 1px solid ${theme.colors.neutral[200]};
    `}

  ${(props) =>
    props.size === 'sm' &&
    css`
      padding: 4px 8px;
      font-size: ${theme.fontSizes.sm}px;
    `}

  &:not(:disabled):hover,
  &:not(:disabled):focus {
    box-shadow: 0px 0px 0px 3px ${theme.colors.blue[300]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }
`

const IconContainer = styled.div`
  margin-left: 8px;
  color: ${theme.colors.blue[200]};
`

export { Button }
